import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  WatchListPage as SharedWatchListPage,
  detectionImageKey,
  reportModelSlug,
} from '@trinetra/shared';
import { getNodeCameraNames, getNodeModelEvent, getNodeModelEventImage } from '../services/api.service';
import { EventDetailBody, EventDetailTitle } from './AlertInvestigationPage';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * Watch List for the selected site.
 *
 * SITE-SCOPED, like every other analytics page here: a watch list belongs to a node, because
 * that is where the faces are enrolled and the plates are matched. With no site chosen the
 * shared page renders its "select a site" state rather than an empty list — "nobody is being
 * watched for" and "you have not told me which site" are different answers.
 *
 * `key={nodeId}` remounts on a site switch so no row, tab or page position from the previous
 * site survives into the next one.
 */
/** A sighting's own report model — the two kinds come from different tables. */
const modelOf = (kind: string) => reportModelSlug(kind === 'person' ? 'face' : 'vehicle_crossing');

export default function WatchListPage() {
  const { selectedNode } = useNode();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const nodeId = selectedNode?.nodeId || '';

  /**
   * Camera id → name for the selected site, for the feed's Camera column AND the record the
   * modal opens. Without it the shared page can only fall back to the raw camera id — which is
   * what the cloud showed until this was passed (the local copy of this page always had it).
   * Reset on a site switch: another site's names would resolve nothing, or the wrong camera.
   *
   * The names-only endpoint, like the local page: it needs just `cameras.list`, where the full
   * camera list needs `cameras.view` — a role given this page (gated on `ai_events.*`) but not
   * camera details would otherwise 403 here and be back to ids.
   */
  const [cameraNames, setCameraNames] = useState<Record<string, string>>({});
  useEffect(() => {
    setCameraNames({});
    if (!nodeId) return undefined;
    let cancelled = false;
    getNodeCameraNames(nodeId)
      .then((res: any) => {
        // The tunnel hands the list back in more than one envelope.
        const list: any[] = Array.isArray(res) ? res : res?.cameras || res?.data || [];
        if (!cancelled) setCameraNames(Object.fromEntries(list.map((c: any) => [String(c.id), c.name || String(c.id)])));
      })
      .catch(() => { /* names are a convenience; the id is still shown */ });
    return () => { cancelled = true; };
  }, [nodeId]);

  return (
    <SharedWatchListPage
      key={nodeId}
      nodeKey={nodeId}
      blocked={!nodeId}
      canEdit={hasPermission('ai_events.edit')}
      cameraName={(id) => cameraNames[id]}
      className="p-6 space-y-4"
      // The two kinds of sighting come from different tables, so each routes to its own
      // report model — the same jump the search results and report tables make.
      /* Now the modal's "Full page" button rather than what a row click does — the record
         opens over the feed, so checking one sighting no longer costs the window, the subject
         filter and the page position. */
      onOpenDetection={(d) =>
        navigate(`/events/${modelOf(d.kind)}/${encodeURIComponent(d.event_id)}`)
      }
      /* The SAME record body the search results and the report tables open, node-scoped like
         everything else here: every read crosses the tunnel to one site. */
      /*
        ── FROM A SIGHTING TO THE TRAIL ──

        The subject rides in the URL as a real filter, not as a typed sentence: the query parser
        recognises a name only if it is in the registry it was handed, so `?q=<label>` would
        silently produce NO filter for anyone it did not know — and an unfiltered Track page
        reads as "this is their whole trail", which is the one answer that must never be wrong
        here. `name`/`plate` are the page's own filter fields, so what arrives is visible as a
        chip and removable like anything else.
      */
      onTrackDetection={(d) => {
        const subject = d.kind === 'vehicle' ? 'vehicle' : 'person';
        const key = subject === 'vehicle' ? 'plate' : 'name';
        navigate(
          `/search/${subject}?mode=track&${key}=${encodeURIComponent(d.subject_label || '')}`,
        );
      }}
      detectionDetail={{
        load: (d) => getNodeModelEvent(nodeId, modelOf(d.kind), d.event_id),
        title: (event, d) => <EventDetailTitle event={event} model={modelOf(d.kind)} />,
        render: (event, d) => (
          <EventDetailBody
            event={event}
            model={modelOf(d.kind)}
            /* What lets the body stream its own snapshots back through the tunnel — the local
               copy needs no such thing, which is why the body is supplied per host. */
            nodeId={nodeId}
            cameraNameMap={cameraNames}
            /* The identity the FEED showed, so the record cannot contradict the row that
               opened it. */
            personName={() => d.subject_label || undefined}
            hint="← → move between sightings · ↑ ↓ move between this event’s frames"
          />
        ),
      }}
      /* Streamed through the tunnel, one row at a time and only as each is about to be on
         screen — `useAlertImage` behind this prop owns the laziness. The cloud has no route to
         a node's object store, so the KEY `detectionImageKey` picked is passed as the image
         TYPE and the node resolves it back to a file (its resolver handles the spelling
         variants, so `person_crop_2` and `personCrop2` both land).

         A tunnel 404 or timeout still resolves as a Blob — of JSON or text — under
         responseType:'blob', so only real image bytes become an object URL. Anything else
         returns null and the row keeps its avatar rather than showing a broken image. */
      detectionLoadThumbnail={async (d) => {
        const key = detectionImageKey(d);
        if (!nodeId || !d.event_id || !key) return null;
        try {
          const model = reportModelSlug(d.kind === 'person' ? 'face' : 'vehicle_crossing');
          const blob: any = await getNodeModelEventImage(nodeId, model, d.event_id, key);
          if (!blob || (blob.type && !blob.type.startsWith('image/'))) return null;
          return URL.createObjectURL(blob);
        } catch {
          return null;
        }
      }}
    />
  );
}
